import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { DutyHourTypeDynFlag } from 'generated';
import { useSelector } from 'hooks';
import { cloneDeep } from 'lodash';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RED } from 'constants/colors';

import {
  getDefaultDHT,
  getReferenceBenefit,
  getReferenceBenefitOrDefault
} from 'components/utils';

import { queries } from 'RestAPIs/rapini';

import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';

import {
  ControlledPlanUnitSelector,
  ControlledSelect,
  ControlledTimePeriods
} from 'components/shared';

import type { WorkAgreementDetails } from 'components/types';
import { type Duty, type Form } from './types';

import { StyledDialog } from '../Dialog/Dialog.styles';

import {
  GridCompact12,
  GridCompact6,
  GridContainer
} from '../Grid/Grid.components';

import dutyDialogSchema from './duty-dialog-schema';
import { createDuty } from './DutyDialog.utils';

const schema = dutyDialogSchema();

const DutyDialogStyles = styled.div`
  h3 {
    margin-top: 0;
  }

  .week-day-btn {
    padding: 0;
    margin: 0 3px;

    &:first-child,
    &:last-child {
      margin: 0;
    }

    .MuiChip-root {
      width: 40px;
      height: 40px;

      .MuiChip-label {
        padding: 0;
      }
    }
  }
`;

type Props = {
  duty?: Duty | null;
  usePublicPlanUnits?: boolean;
  dutyHourTypeDynFlags?: DutyHourTypeDynFlag[];
  workAgreements: WorkAgreementDetails[];
  startDate: Date | null;
  endDate: Date | null;
  onSave: (duty: Duty) => void;
  onHidden: () => void;
  isInEmployeeCardDialog?: boolean;
  referenceDate: Date | null;
  checkRestrictedAuthorization?: boolean;
};

export default function DutyDialog({
  duty: editedDuty,
  usePublicPlanUnits,
  dutyHourTypeDynFlags,
  workAgreements,
  startDate,
  endDate,
  onSave,
  onHidden,
  isInEmployeeCardDialog,
  referenceDate,
  checkRestrictedAuthorization
}: Props) {
  const { t } = useTranslation('common');
  const { planUnits } = useSelector((state) => state);

  const [isOpen, setOpen] = useState(true);
  const { data: dutyHourTypes } = queries.useGetDutyHourTypes(
    referenceDate != null ? referenceDate.toISOString() : undefined,
    checkRestrictedAuthorization
  );

  const isEdit = Boolean(editedDuty);
  const defaultWorkAgreement = workAgreements.filter(
    ({ employmentStart, employmentEnd }) =>
      (startDate === null || startDate < new Date(employmentEnd)) &&
      (endDate === null || endDate > new Date(employmentStart))
  )[0];
  const duty =
    cloneDeep(editedDuty) ||
    createDuty({
      timePeriods: ['00:00', '00:00'],
      dutyHourType: getDefaultDHT(dutyHourTypes),
      workAgreement: defaultWorkAgreement,
      planUnit: planUnits.filter(
        ({ id }) =>
          id ===
          getReferenceBenefitOrDefault(defaultWorkAgreement, startDate, endDate)
            ?.brId
      )[0]
    });

  const { control, handleSubmit, watch, getValues } = useForm<Form>({
    resolver: yupResolver(schema),
    defaultValues: {
      workAgreementId: duty?.workAgreement?.waId,
      planUnit: duty?.planUnit ? duty.planUnit : undefined,
      periods:
        duty?.periods.map((p) => ({
          ...p,
          startTime: dayjs(p.startTime),
          endTime: dayjs(p.endTime)
        })) || []
    }
  });

  function onSubmit(data) {
    const workAgreement = workAgreements.filter(
      (wa) => wa.waId === data.workAgreementId
    )[0];

    const newDuty = {
      id: duty.id,
      workAgreement,
      planUnit: data.planUnit,
      periods: data.periods.map((p) => ({
        ...p,
        startTime: p.startTime.toISOString(),
        endTime: p.endTime.toISOString()
      }))
    };

    onSave(newDuty);
    setOpen(false);
  }

  const totalHours = watch('periods').reduce((acc, p) => {
    if (!p.startTime || !p.endTime) return 0;
    acc += p.endTime.diff(p.startTime, 'hour');
    return acc;
  }, 0);

  const workAgreementOptions = workAgreements.map((workAgreement) => {
    const benefit = getReferenceBenefitOrDefault(
      workAgreement,
      startDate,
      endDate
    );
    const isValid = !!getReferenceBenefit(workAgreement, startDate, endDate);
    return {
      label: `${workAgreement.nr} - ${benefit?.functionDescription} - ${benefit?.brName}`,
      value: workAgreement.waId,
      style: !isValid ? ({ color: RED } as React.CSSProperties) : undefined
    };
  });

  return (
    <StyledDialog
      title={isEdit ? t('editDuty') : t('addDuty')}
      open={isOpen}
      onHidden={onHidden}
      disablePortal={isInEmployeeCardDialog}
      slotProps={{
        paper: {
          sx: {
            maxWidth: 700
          }
        }
      }}
    >
      <DutyDialogStyles>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <GridContainer>
            {workAgreements.length > 1 && (
              <GridCompact6>
                <ControlledSelect
                  control={control}
                  label={t('workAgreement')}
                  name="workAgreementId"
                  options={workAgreementOptions}
                />
              </GridCompact6>
            )}
            <GridCompact6>
              <ControlledPlanUnitSelector
                control={control}
                variant="outlined"
                name="planUnit"
                isSingleSelect
                hideGroups
                hideNavigation
                doNotPersist
                fullWidth
                defaultValue={duty.planUnit ? [duty.planUnit] : undefined}
                usePublicPlanUnits={usePublicPlanUnits}
              />
            </GridCompact6>
            <GridCompact12>
              <ControlledTimePeriods
                control={control}
                watch={watch}
                dutyHourTypeDynFlags={dutyHourTypeDynFlags}
                hasDHTSelector
                baseDate={getValues('periods')[0]?.startTime?.startOf('day')}
                referenceDate={
                  referenceDate != null ? referenceDate : undefined
                }
                checkRestrictedAuthorization={checkRestrictedAuthorization}
              />
            </GridCompact12>
          </GridContainer>

          <DialogActions className="dialog-actions">
            <Button
              onClick={() => setOpen(false)}
              color="primary"
              variant="outlined"
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={totalHours > 24}
            >
              {t('save')}
            </Button>
          </DialogActions>
        </form>
      </DutyDialogStyles>
    </StyledDialog>
  );
}
